import { RestRequest, RestContext, ResponseFunction } from 'msw'
import { base64UrlEncode } from '~/cynqueModules/base64UrlEncode'
import { AuthenticateUserResponse200 } from '~/services/users'
import { readAuthMethods } from '../passkeys/mockPasskeyState'

// Sentinel password that lets tests exercise the bad-credentials path.
// Any other non-empty password authenticates successfully.
const BAD_CREDENTIALS_PASSWORD = 'wrongpassword'

export async function postUserAuthentication(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  let username = ''
  let password = ''
  try {
    const body = await req.json()
    username = body?.username ?? ''
    password = body?.password ?? ''
  } catch (e) {
    // fall through to the invalid-credentials response below
  }

  // A deployment with PASSWORD_LOGIN_ENABLED=false refuses every password with
  // the same 403 as a wrong one; e2e switches it off via mock:authMethods.
  const isPasswordLoginEnabled = readAuthMethods().includes('password')

  if (
    !isPasswordLoginEnabled ||
    !username ||
    !password ||
    password === BAD_CREDENTIALS_PASSWORD
  ) {
    // The real api's body, byte for byte: RFC 9457 problem details. This mock
    // used to send `{errorCode, message}`, a shape the api had stopped sending,
    // which kept the login page's matching on it green here while every real
    // wrong password showed "Unknown type of user authentication error".
    return res(
      ctx.status(403),
      ctx.json({
        status: 403,
        type: 'user_authentication/invalid_credentials',
        title: 'User authentication failure',
        detail: 'Credentials are invalid.',
      })
    )
  }

  // The real api's envelope is `{ data: { authToken } }`: its model has a
  // getUser field, but the serializer leaves it out (api #1698).
  const responseBody: { data: AuthenticateUserResponse200 } = {
    data: { authToken: fakeToken() },
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

export function fakeToken() {
  const header = {
    alg: 'none',
    typ: 'JWT',
  }
  const encodedHeader = base64UrlEncode(JSON.stringify(header))
  // 5-minute expiry, mirroring the real token.
  const claims = {
    exp: Math.floor(Date.now() / 1000) + 60 * 5,
  }
  const encodedPayload = base64UrlEncode(JSON.stringify(claims))
  // 'none' alg -> empty signature segment.
  const encodedSignature = base64UrlEncode('')
  return `${encodedHeader}.${encodedPayload}.${encodedSignature}`
}
